/**
 * CardChip — compact card representation rendered within calendar day cells.
 * Shows a colored left border (from list color), truncated title, and supports
 * keyboard activation, drag initiation, keyboard-based rescheduling, and
 * accessible labeling.
 *
 * Validates: Requirements 3.6, 5.1, 5.2, 5.3, 6.5, 10.2, 10.4, 10.6
 */

import React, { useCallback, useRef, useState } from 'react';
import { CardSummary } from '../../state/active-board-context';
import { formatDateForAria } from '../../utils/date-utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CardChipProps {
  readonly card: CardSummary;
  readonly listColor: string;
  readonly onClick: () => void;
  /** Called when the user moves the card left/right during keyboard reschedule mode. */
  readonly onRescheduleMove?: (cardId: string, direction: 'left' | 'right') => void;
  /** Called when the user confirms the reschedule (Enter in reschedule mode). */
  readonly onRescheduleConfirm?: (cardId: string) => void;
  /** Called when the user cancels the reschedule (Escape in reschedule mode). */
  readonly onRescheduleCancel?: (cardId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CardChip({
  card,
  listColor,
  onClick,
  onRescheduleMove,
  onRescheduleConfirm,
  onRescheduleCancel,
}: CardChipProps): React.ReactElement {
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const liveRegionRef = useRef<HTMLSpanElement>(null);

  const ariaLabel = card.dueDate
    ? `${card.title}, due ${formatDateForAria(card.dueDate)}`
    : card.title;

  const announce = useCallback((message: string) => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = message;
    }
  }, []);

  const enterRescheduleMode = useCallback(() => {
    setIsRescheduleMode(true);
    announce(
      `Rescheduling ${card.title}. Use arrow keys to change date, Enter to confirm, Escape to cancel.`,
    );
  }, [card.title, announce]);

  const exitRescheduleMode = useCallback(
    (confirmed: boolean) => {
      setIsRescheduleMode(false);
      if (confirmed) {
        announce(`Reschedule confirmed for ${card.title}.`);
        onRescheduleConfirm?.(card.cardId);
      } else {
        announce(`Reschedule cancelled for ${card.title}.`);
        onRescheduleCancel?.(card.cardId);
      }
    },
    [card.cardId, card.title, announce, onRescheduleConfirm, onRescheduleCancel],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isRescheduleMode) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            e.stopPropagation();
            onRescheduleMove?.(card.cardId, 'left');
            announce(`Moving ${card.title} to previous day.`);
            break;
          case 'ArrowRight':
            e.preventDefault();
            e.stopPropagation();
            onRescheduleMove?.(card.cardId, 'right');
            announce(`Moving ${card.title} to next day.`);
            break;
          case 'Enter':
            e.preventDefault();
            e.stopPropagation();
            exitRescheduleMode(true);
            break;
          case 'Escape':
            e.preventDefault();
            e.stopPropagation();
            exitRescheduleMode(false);
            break;
          default:
            // Consume other keys to avoid grid navigation while rescheduling
            e.stopPropagation();
            break;
        }
        return;
      }

      // Normal mode: Enter/Space activates click, second Enter enters reschedule mode
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        // Enter on a focused CardChip enters reschedule mode if callbacks are provided
        if (onRescheduleMove) {
          enterRescheduleMode();
        } else {
          onClick();
        }
      } else if (e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }
    },
    [
      isRescheduleMode,
      card.cardId,
      card.title,
      onClick,
      onRescheduleMove,
      enterRescheduleMode,
      exitRescheduleMode,
      announce,
    ],
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.dataTransfer.setData('text/plain', card.cardId);
      e.dataTransfer.effectAllowed = 'move';
    },
    [card.cardId],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={isRescheduleMode}
      draggable={!isRescheduleMode}
      className={`flex items-center px-2 py-1 rounded text-xs cursor-pointer
        bg-card/80 border border-border/40 shadow-sm
        hover:bg-accent hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500
        transition-all select-none min-w-0
        ${isRescheduleMode ? 'ring-2 ring-offset-1 ring-yellow-500 bg-yellow-50 dark:bg-yellow-900/30' : ''}`}
      style={{ borderLeft: `3px solid ${listColor}` }}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onDragStart={handleDragStart}
    >
      <span className="truncate text-card-foreground">{card.title}</span>
      {/* Aria-live region for screen reader announcements */}
      <span
        ref={liveRegionRef}
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      />
    </div>
  );
}
